/**
 * @file date-time-pickers/shared/animation-tokens.ts
 *
 * MD3 Expressive animation constants for picker overlays.
 * Reuses shared motion-tokens from ui/shared and adds picker-specific animations.
 *
 * CSS variables sourced from packages/tailwind/src/index.css.
 *
 * @see https://m3.material.io/styles/motion/overview/how-it-works
 */

import type { TargetAndTransition, Transition, Variants } from "motion/react";
import {
	DEFAULT_EFFECTS_SPRING,
	DEFAULT_SPATIAL_SPRING,
	FAST_EFFECTS_SPRING,
	FAST_SPATIAL_SPRING,
} from "../../shared/motion-tokens";

// ─── Dialog / Overlay Animations ─────────────────────────────────────────────

/** Scrim fade animation — 200ms standard-decelerate */
export const PICKER_OVERLAY_ANIM: {
	initial: TargetAndTransition;
	animate: TargetAndTransition;
	exit: TargetAndTransition;
} = {
	initial: { opacity: 0 },
	animate: {
		opacity: 1,
		transition: { duration: 0.2, ease: [0, 0, 0, 1] as const },
	},
	exit: {
		opacity: 0,
		transition: { duration: 0.15, ease: [0.3, 0, 1, 1] as const },
	},
};

/**
 * Content panel enter animation — MD3 default.spatial spring (380/0.8).
 * Scales from 0.85 to 1 + fade in.
 */
export const PICKER_CONTENT_ANIM: {
	initial: TargetAndTransition;
	animate: TargetAndTransition;
	exit: TargetAndTransition;
} = {
	initial: { opacity: 0, scale: 0.85, y: 16 },
	animate: {
		opacity: 1,
		scale: 1,
		y: 0,
		transition: DEFAULT_SPATIAL_SPRING,
	},
	exit: {
		opacity: 0,
		scale: 0.95,
		y: 8,
		transition: { duration: 0.15, ease: [0.3, 0, 1, 1] as const },
	},
};

// ─── Calendar Grid Navigation ─────────────────────────────────────────────────

/**
 * Slide animation when navigating to next month (→).
 * FAST_SPATIAL_SPRING — 350ms, cubic-bezier(0.42, 1.67, 0.21, 0.9).
 */
export const CALENDAR_SLIDE_VARIANTS: Variants = {
	enterFromRight: {
		x: "100%",
		opacity: 0,
	},
	enterFromLeft: {
		x: "-100%",
		opacity: 0,
	},
	center: {
		x: 0,
		opacity: 1,
		transition: FAST_SPATIAL_SPRING,
	},
	exitToLeft: {
		x: "-100%",
		opacity: 0,
		transition: { duration: 0.15, ease: [0.3, 0, 1, 1] as const },
	},
	exitToRight: {
		x: "100%",
		opacity: 0,
		transition: { duration: 0.15, ease: [0.3, 0, 1, 1] as const },
	},
};

// ─── Year / Month Selector Grid ───────────────────────────────────────────────

/**
 * Month and Year selector panel transition — DEFAULT_SPATIAL_SPRING (380/0.8).
 * Scales from 0.95 to 1 + subtle vertical spring shift.
 */
export const PICKER_SELECTOR_ANIM: {
	initial: TargetAndTransition;
	animate: TargetAndTransition;
	exit: TargetAndTransition;
} = {
	initial: { opacity: 0, scale: 0.95, y: -6 },
	animate: {
		opacity: 1,
		scale: 1,
		y: 0,
		transition: DEFAULT_SPATIAL_SPRING,
	},
	exit: {
		opacity: 0,
		scale: 0.95,
		y: 6,
		transition: { duration: 0.1, ease: "easeIn" as const },
	},
};

/** Year grid fade in/out — DEFAULT_EFFECTS_SPRING (1600/1.0) */
export const YEAR_GRID_ANIM: {
	initial: TargetAndTransition;
	animate: TargetAndTransition;
	exit: TargetAndTransition;
} = {
	initial: { opacity: 0, scale: 0.95 },
	animate: {
		opacity: 1,
		scale: 1,
		transition: DEFAULT_EFFECTS_SPRING,
	},
	exit: {
		opacity: 0,
		scale: 0.95,
		transition: { duration: 0.1, ease: "easeIn" as const },
	},
};

// ─── Day Cell ─────────────────────────────────────────────────────────────────

/** State layer transition cho day cell — FAST_EFFECTS_SPRING (3800/1.0) */
export const DAY_CELL_STATE_TRANSITION: Transition = FAST_EFFECTS_SPRING;

// ─── Re-exports for convenience ───────────────────────────────────────────────

export {
	DEFAULT_EFFECTS_SPRING,
	DEFAULT_SPATIAL_SPRING,
	FAST_EFFECTS_SPRING,
	FAST_SPATIAL_SPRING,
};
